import { useState, useRef, createContext, useContext } from 'react';
import { motion, useInView, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { FloatingNav, OverlaySideNav, HeroSection as LayoutHeroSection, NavSection } from '@/components/layout';
import {
  PillarCalculator,
  ScenarioPresets,
  DataTable,
  DataTableHeader,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  MeasureIndicator,
  HighlightValue,
} from '@/components/equip';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import {
  Scale,
  Target,
  Users,
  Ban,
  ChevronRight,
  Book,
  HelpCircle,
  Calculator,
  Gauge,
  DollarSign,
  Calendar,
  TrendingUp,
  Shield,
  AlertTriangle,
  FileText,
  Percent,
  ClipboardList,
  Stethoscope,
  Brain,
  Heart,
  Phone,
  Info,
  UserCheck,
  Briefcase,
  XCircle,
  CheckCircle2,
} from 'lucide-react';
import { getAllAPCEligibilities } from '@/data';
import { NON_PHYSICIAN_ROLE_CONFIG, type NonPhysicianRole } from '@/lib/equip-program-config';
import type { EmploymentStatus } from '@/data/types';
import { useEquipMeasureScheduleYear } from '@/hooks/equip/useEquipMeasureScheduleYear';
import {
  getRoleQuarterOutcomeMeasures,
  getMeasureThresholdDisplay,
  hasSingleMeasureRisk,
  roleToBonusEligibleType,
  type ScheduleRole,
  type ScheduleQuarter,
} from '@/lib/measureScheduleSelectors';
import type { MeasureScheduleItem } from '@/lib/equip-types';

// Re-export NonPhysicianRole for consumers (e.g. ClinicianFrameworkGateway.tsx)
export type { NonPhysicianRole } from '@/lib/equip-program-config';

// ============================================================================
// Types and Configuration
// ============================================================================

// Local alias — all sections using roleConfig[role] continue to work unchanged.
// The canonical config shape is a superset of the old inline RoleConfig (adds id,
// category, iconName) — extra fields are harmless.
const roleConfig = NON_PHYSICIAN_ROLE_CONFIG;

const ALL_QUARTERS: ScheduleQuarter[] = ['Q1', 'Q2', 'Q3', 'Q4'];

// ---------------------------------------------------------------------------
// Local selector helpers
// ---------------------------------------------------------------------------

/**
 * Returns the distinct consistency measure names for a role across all quarters.
 * Consistency rows have measureCategory === 'Consistency'.
 */
function getRoleConsistencyMeasures(
  schedule: MeasureScheduleItem[],
  role: ScheduleRole,
): string[] {
  const bet = roleToBonusEligibleType(role);
  const seen = new Set<string>();
  schedule.forEach(row => {
    if (row.isActive && row.bonusEligibleType === bet && row.measureCategory === 'Consistency') {
      seen.add(row.measureName);
    }
  });
  return Array.from(seen);
}


// Context for propagating the locked role, employment status, and schedule to child sections
interface NonPhysicianFrameworkContextValue {
  lockedRoleId?: NonPhysicianRole;
  employmentStatus?: EmploymentStatus;
  /** Flattened measure schedule for the current year — all four quarters. */
  scheduleItems: MeasureScheduleItem[];
  scheduleLoading: boolean;
}
const NonPhysicianFrameworkContext = createContext<NonPhysicianFrameworkContextValue>({
  scheduleItems: [],
  scheduleLoading: true,
});
const useNonPhysicianContext = () => useContext(NonPhysicianFrameworkContext);

// Navigation sections
const navSections: NavSection[] = [
  { id: 'quick-reference', label: 'Quick Reference', icon: <FileText className="w-4 h-4" /> },
  { id: 'eligibility', label: 'Who Qualifies', icon: <UserCheck className="w-4 h-4" /> },
  { id: 'sixty-forty-model', label: 'The 60/40 Model', icon: <Percent className="w-4 h-4" /> },
  { id: 'role-comparison', label: 'Role Comparison', icon: <Users className="w-4 h-4" /> },
  { id: 'quarterly-schedule', label: 'Quarterly Schedule', icon: <Calendar className="w-4 h-4" /> },
  { id: 'maximum-award', label: 'Maximum Award', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'measure-definitions', label: 'Measure Definitions', icon: <ClipboardList className="w-4 h-4" /> },
  { id: 'ninety-percent', label: 'The 90% Threshold', icon: <Target className="w-4 h-4" /> },
  { id: 'calculation-steps', label: 'How Awards Calculate', icon: <Calculator className="w-4 h-4" /> },
  { id: 'payout-scenarios', label: 'Payout Scenarios', icon: <TrendingUp className="w-4 h-4" /> },
  { id: 'interactive-calculator', label: 'Calculator', icon: <Calculator className="w-4 h-4" /> },
  { id: 'scenario-presets', label: 'Practice Scenarios', icon: <Users className="w-4 h-4" /> },
  { id: 'risk-profile', label: 'Risk Profile', icon: <Shield className="w-4 h-4" /> },
  { id: 'key-messages', label: 'Key Messages', icon: <Book className="w-4 h-4" /> },
  { id: 'next-steps', label: 'Next Steps', icon: <ChevronRight className="w-4 h-4" /> },
];

// Animation variants for fade-in on scroll
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// Section wrapper with scroll margin
function Section({
  id,
  children,
  className,
  background = 'white',
}: {
  id: string;
  children: React.ReactNode;
  className?: string;
  background?: 'white' | 'gray';
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <section
      id={id}
      ref={ref}
      className={cn(
        'py-12 md:py-16 scroll-mt-20',
        background === 'gray' ? 'bg-gray-50' : 'bg-white',
        className
      )}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        variants={fadeInUp}
        className="max-w-6xl mx-auto px-6"
      >
        {children}
      </motion.div>
    </section>
  );
}

// Quick Reference Card for Non-Physicians
function QuickReferenceCard({ className }: { className?: string }) {
  const { lockedRoleId, employmentStatus } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;
  const isPartTime = employmentStatus === 'part-time';

  // Employment-aware maximum award display
  const maxAwardValue = config
    ? isPartTime
      ? config.maxAward.perHourPartTime
        ? `$${config.maxAward.perHourPartTime}/hr x hrs`
        : `$${config.maxAward.perDayPartTime}/day x days`
      : `$${config.maxAward.fullTime.toLocaleString()}`
    : null;
  const maxAwardSublabel = config
    ? isPartTime
      ? 'per quarter (part-time)'
      : 'per quarter (full-time)'
    : null;

  const lockedStats = config
    ? [
        {
          label: 'Your Maximum',
          value: maxAwardValue!,
          sublabel: maxAwardSublabel!,
          icon: DollarSign,
          color: 'bg-eventus-teal/10 text-eventus-teal',
        },
        {
          label: 'Award Split',
          value: '60/40',
          sublabel: 'Outcomes + Consistency',
          icon: Percent,
          color: 'bg-pink-100 text-pink-600',
        },
        {
          label: config.id === 'teletriage'
            ? 'ED Send-Out Threshold'
            : config.id === 'primary-care-apc' ? 'Measures' : 'Threshold',
          value: config.id === 'teletriage'
            ? '<25%'
            : config.id === 'primary-care-apc' ? 'See quarterly' : '90%',
          sublabel: config.id === 'teletriage'
            ? 'lower is better'
            : config.id === 'primary-care-apc' ? 'schedule below' : 'for all measures',
          icon: Target,
          color: 'bg-blue-100 text-blue-600',
        },
      ]
    : null;

  const defaultStats = [
    {
      label: 'APC Maximum',
      value: '$3,000',
      sublabel: 'per quarter',
      icon: DollarSign,
      color: 'bg-eventus-teal/10 text-eventus-teal',
    },
    {
      label: 'Teletriage Maximum',
      value: '$1,500',
      sublabel: 'per quarter',
      icon: Phone,
      color: 'bg-eventus-navy/10 text-eventus-navy',
    },
    {
      label: 'Award Split',
      value: '60/40',
      sublabel: 'Outcomes + Consistency',
      icon: Percent,
      color: 'bg-pink-100 text-pink-600',
    },
    {
      label: 'Universal Threshold',
      value: '90%',
      sublabel: 'for all measures',
      icon: Target,
      color: 'bg-blue-100 text-blue-600',
    },
  ];

  const stats = lockedStats ?? defaultStats;

  return (
    <Card className={cn('border-2 border-eventus-teal/20', className)}>
      <CardContent className="p-6">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Quick Reference
        </h2>
        <div className={cn('grid gap-4', config ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-4')}>
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div
                className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2',
                  stat.color
                )}
              >
                <stat.icon className="w-5 h-5" />
              </div>
              <div className="text-2xl font-bold text-eventus-navy">
                {stat.value}
              </div>
              <div className="text-xs text-gray-500">{stat.sublabel}</div>
              <div className="text-xs font-medium text-gray-700 mt-1">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Hero Section using shared component
function HeroSection() {
  const { lockedRoleId } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;

  return (
    <LayoutHeroSection
      role="non-physician"
      badgeText={config ? `${config.name} Framework` : 'Non-Physician Framework'}
      title={config ? `EQuIP 2026 BETA — ${config.name}` : 'EQuIP 2026 BETA — APC Framework'}
      description={
        config
          ? 'Your role-specific measures, thresholds, and award calculations.'
          : 'Reference guide for leadership. Role-specific measures and awards for Advanced Practice Clinicians.'
      }
      cta={{
        label: 'Jump to Calculator',
        icon: <Calculator className="w-4 h-4" />,
        onClick: () => {
          const element = document.getElementById('interactive-calculator');
          if (element) {
            const yOffset = -80;
            const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        },
      }}
    />
  );
}

// Eligibility Section for APCs
function APCEligibilitySection() {
  const { employmentStatus } = useNonPhysicianContext();
  const eligibilities = getAllAPCEligibilities();
  // All APCs share the same criteria, so just use the first one
  const sharedCriteria = eligibilities[0].criteria;
  const sharedExclusions = eligibilities[0].exclusions;

  const criteriaIcons = [UserCheck, Briefcase, TrendingUp];

  return (
    <Section id="eligibility" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-6">
        Who Qualifies for EQuIP
      </h2>

      <div className="space-y-6">
        {/* Eligibility Criteria */}
        <Card className="border-2 border-gray-200 overflow-hidden">
          <div className="px-6 py-3 bg-eventus-teal">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5" />
              Who Qualifies
            </h3>
          </div>
          <CardContent className="p-6">
            <div className="space-y-4">
              {sharedCriteria.map((criterion, index) => {
                const Icon = criteriaIcons[index] || CheckCircle2;
                return (
                  <div key={criterion.label} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-eventus-teal/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-eventus-teal-dark" />
                    </div>
                    <div>
                      <div className="font-semibold text-eventus-navy">
                        {criterion.label}
                      </div>
                      <div className="text-sm text-gray-600">
                        {criterion.description}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Award Amounts by Role */}
        <Card className="border-2 border-gray-200">
          <CardContent className="p-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Maximum Quarterly Award by Role
            </h3>
            <DataTable>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableCell header>Role</DataTableCell>
                  {employmentStatus !== 'part-time' && (
                    <DataTableCell header align="center">Full-Time</DataTableCell>
                  )}
                  {employmentStatus !== 'full-time' && (
                    <DataTableCell header align="center">Part-Time Rate</DataTableCell>
                  )}
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {eligibilities.map((elig) => {
                  const RoleIcon =
                    elig.shortName === 'PC APC'
                      ? Stethoscope
                      : elig.shortName === 'Psych APC'
                        ? Brain
                        : elig.shortName === 'Pall APC'
                          ? Heart
                          : Phone;
                  return (
                    <DataTableRow key={elig.roleName}>
                      <DataTableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <RoleIcon className="w-4 h-4 text-gray-500" />
                          {elig.roleName}
                        </div>
                      </DataTableCell>
                      {employmentStatus !== 'part-time' && (
                        <DataTableCell align="center">
                          <HighlightValue color="teal">
                            ${elig.maxAward.fullTime.toLocaleString()}
                          </HighlightValue>
                        </DataTableCell>
                      )}
                      {employmentStatus !== 'full-time' && (
                        <DataTableCell align="center">
                          {elig.maxAward.perHour
                            ? `$${elig.maxAward.perHour}/hr`
                            : `$${elig.maxAward.perDay}/day`}
                        </DataTableCell>
                      )}
                    </DataTableRow>
                  );
                })}
              </DataTableBody>
            </DataTable>

            {/* Part-time example — hidden for full-time employees */}
            {employmentStatus !== 'full-time' && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-800">
                  <strong>Part-time example:</strong> A Primary Care APC working 3 days/week
                  would have a maximum award potential of <strong>$1,800</strong> per quarter ($600 × 3 days).
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Exclusions */}
        {sharedExclusions.length > 0 && (
          <Card className="border-2 border-red-100 bg-red-50/30">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-red-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                <XCircle className="w-4 h-4" />
                Not Eligible
              </h3>
              <div className="space-y-3">
                {sharedExclusions.map((exclusion) => (
                  <div
                    key={exclusion.label}
                    className="flex items-start gap-3 text-red-900"
                  >
                    <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="font-medium">{exclusion.label}</div>
                      <div className="text-sm text-red-700">{exclusion.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Section>
  );
}

// 60/40 Model Section
function SixtyFortyModelSection() {
  const { lockedRoleId, scheduleItems } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;
  const maxAward = config ? config.maxAward.fullTime : 3000;
  const consistencyMeasureNames = config
    ? getRoleConsistencyMeasures(scheduleItems, lockedRoleId! as ScheduleRole)
    : [];
  const consistencyLabel = consistencyMeasureNames.length > 0
    ? consistencyMeasureNames.join(', ')
    : config
      ? (config.hasPIMs ? 'PIMs, Coding, Sig Stnds' : 'Coding, Sig Stnds')
      : 'PIMs, Coding, Sig Standards';
  const outcomesMax = Math.round(maxAward * 0.6);
  const consistencyMax = Math.round(maxAward * 0.4);
  const columnHeader = config ? `Max (${config.shortName})` : 'Max (APC)';

  return (
    <Section id="sixty-forty-model">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        The 60/40 Model
      </h2>
      <p className="text-gray-600 mb-6">
        The award is built from two independent parts. Meet or exceed a measure, earn that portion of the incentive. If a measure is not met, only that portion of the incentive is not earned.
      </p>

      {/* Split Bar Visualization - Static */}
      <div className="rounded-xl overflow-hidden border-2 border-gray-200 shadow-sm mb-6">
        <div className="flex">
          <div className="w-[60%] bg-pink-500 text-white p-6">
            <div className="text-3xl font-bold">60%</div>
            <div className="text-lg font-semibold">OUTCOMES</div>
            <div className="text-sm text-white/80">
              Clinical quality measures tied to ACO success
            </div>
          </div>
          <div className="w-[40%] bg-blue-500 text-white p-6">
            <div className="text-3xl font-bold">40%</div>
            <div className="text-lg font-semibold">CONSISTENCY</div>
            <div className="text-sm text-white/80">{consistencyLabel}</div>
          </div>
        </div>
      </div>

      {/* Component Breakdown Table */}
      <DataTable>
        <DataTableHeader>
          <DataTableRow>
            <DataTableCell header>Component</DataTableCell>
            <DataTableCell header align="center">Weight</DataTableCell>
            <DataTableCell header>What It Measures</DataTableCell>
            <DataTableCell header align="right">{columnHeader}</DataTableCell>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          <DataTableRow>
            <DataTableCell className="font-medium">Outcomes</DataTableCell>
            <DataTableCell align="center">
              <HighlightValue color="pink">60%</HighlightValue>
            </DataTableCell>
            <DataTableCell>Quality measures tied to patient care</DataTableCell>
            <DataTableCell align="right">${outcomesMax.toLocaleString()}</DataTableCell>
          </DataTableRow>
          <DataTableRow>
            <DataTableCell className="font-medium">Consistency</DataTableCell>
            <DataTableCell align="center">
              <HighlightValue color="blue">40%</HighlightValue>
            </DataTableCell>
            <DataTableCell>{consistencyLabel}</DataTableCell>
            <DataTableCell align="right">${consistencyMax.toLocaleString()}</DataTableCell>
          </DataTableRow>
        </DataTableBody>
      </DataTable>

      {/* How to explain */}
      <Card className="mt-6 bg-eventus-navy/5 border-eventus-navy/20">
        <CardContent className="p-4">
          <p className="text-base text-gray-600">
            <span className="font-semibold text-eventus-navy">How to explain:</span>{' '}
            <span className="italic">
              "Think of it as two buckets. The Outcome bucket is worth 60%, the Consistency bucket is worth 40%. They fill separately. If someone does not meet an Outcome measure, there is no impact to their potential to receive an incentive based on the Consistency measures."
            </span>
          </p>
        </CardContent>
      </Card>
    </Section>
  );
}

// Role Comparison Section
function RoleComparisonSection() {
  const { employmentStatus } = useNonPhysicianContext();

  return (
    <Section id="role-comparison" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Role Comparison
      </h2>
      <p className="text-gray-600 mb-6">
        Different roles have different Outcome Measures, but all follow the same 60/40 structure.
      </p>

      <DataTable className="mb-6">
        <DataTableHeader>
          <DataTableRow>
            <DataTableCell header>Role</DataTableCell>
            {employmentStatus !== 'part-time' && (
              <DataTableCell header align="center">FT Max</DataTableCell>
            )}
            {employmentStatus !== 'full-time' && (
              <DataTableCell header align="center">PT Formula</DataTableCell>
            )}
            <DataTableCell header>Outcome Measures</DataTableCell>
            <DataTableCell header align="center">Has PIMs?</DataTableCell>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          <DataTableRow>
            <DataTableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Stethoscope className="w-4 h-4 text-eventus-teal" />
                Primary Care APC
              </div>
            </DataTableCell>
            {employmentStatus !== 'part-time' && (
              <DataTableCell align="center">
                <HighlightValue>$3,000</HighlightValue>
              </DataTableCell>
            )}
            {employmentStatus !== 'full-time' && (
              <DataTableCell align="center">$600/day</DataTableCell>
            )}
            <DataTableCell>AWV, A1C, BCS, CRCS, PHQ-9, HTN</DataTableCell>
            <DataTableCell align="center">
              <span className="text-green-600">Yes</span>
            </DataTableCell>
          </DataTableRow>
          <DataTableRow>
            <DataTableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Brain className="w-4 h-4 text-eventus-purple" />
                Psychiatry APC
              </div>
            </DataTableCell>
            {employmentStatus !== 'part-time' && (
              <DataTableCell align="center">
                <HighlightValue>$3,000</HighlightValue>
              </DataTableCell>
            )}
            {employmentStatus !== 'full-time' && (
              <DataTableCell align="center">$600/day</DataTableCell>
            )}
            <DataTableCell>PHQ-9 only</DataTableCell>
            <DataTableCell align="center">
              <span className="text-green-600">Yes</span>
            </DataTableCell>
          </DataTableRow>
          <DataTableRow>
            <DataTableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4 text-rose-500" />
                Palliative Care APC
              </div>
            </DataTableCell>
            {employmentStatus !== 'part-time' && (
              <DataTableCell align="center">
                <HighlightValue>$3,000</HighlightValue>
              </DataTableCell>
            )}
            {employmentStatus !== 'full-time' && (
              <DataTableCell align="center">$600/day</DataTableCell>
            )}
            <DataTableCell>ACP only</DataTableCell>
            <DataTableCell align="center">
              <span className="text-green-600">Yes</span>
            </DataTableCell>
          </DataTableRow>
          <DataTableRow>
            <DataTableCell className="font-medium">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-eventus-navy" />
                Teletriage
              </div>
            </DataTableCell>
            {employmentStatus !== 'part-time' && (
              <DataTableCell align="center">
                <HighlightValue color="blue">$1,500</HighlightValue>
              </DataTableCell>
            )}
            {employmentStatus !== 'full-time' && (
              <DataTableCell align="center">$37.50/hr</DataTableCell>
            )}
            <DataTableCell>ED Send-Out only</DataTableCell>
            <DataTableCell align="center">
              <span className="text-red-600">No</span>
            </DataTableCell>
          </DataTableRow>
        </DataTableBody>
      </DataTable>

      {/* Key Differences Callout */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800 mb-1">Specialty Measure Focus</p>
              <p className="text-blue-700 text-sm">
                Psychiatry, Palliative, and Teletriage have one focused Outcome Measure all year, representing 60% of the incentive opportunity.
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800 mb-1">Teletriage Difference</p>
              <p className="text-blue-700 text-sm">
                Teletriage has no PIMs measure because they don't prescribe. Only 2 Consistency measures at 20% each.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Section>
  );
}

// Quarterly Measure Schedule Section
function QuarterlyScheduleSection() {
  const { lockedRoleId, scheduleItems, scheduleLoading } = useNonPhysicianContext();
  const [selectedRole, setSelectedRole] = useState<NonPhysicianRole>('primary-care-apc');
  // When locked, always use the locked role (ignores stale useState initial value)
  const effectiveRole = lockedRoleId ?? selectedRole;
  const config = roleConfig[effectiveRole];
  const scheduleRole = effectiveRole as ScheduleRole;

  // Derive outcome measures per-quarter from the schedule
  const quarters = ALL_QUARTERS;
  const outcomeMeasuresByQuarter = Object.fromEntries(
    quarters.map(q => [q, getRoleQuarterOutcomeMeasures(scheduleItems, scheduleRole, q)])
  ) as Record<ScheduleQuarter, string[]>;
  const allMeasures = Array.from(
    new Set(quarters.flatMap(q => outcomeMeasuresByQuarter[q]))
  );
  // Single-measure risk: true if all 4 quarters have exactly 1 outcome measure
  const allQuartersSingleMeasure = !scheduleLoading &&
    quarters.every(q => hasSingleMeasureRisk(scheduleItems, scheduleRole, q));

  return (
    <Section id="quarterly-schedule">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Quarterly Measure Schedule
      </h2>
      <p className="text-gray-600 mb-6">
        The 60% Outcome portion is divided equally among active measures.
      </p>

      {/* Role Selector — hidden when role is locked (clinician view) */}
      {!lockedRoleId && (
        <div className="mb-6">
          <label className="text-sm font-medium text-gray-700 mb-2 block">Select Role</label>
          <Select
            value={selectedRole}
            onValueChange={(v) => setSelectedRole(v as NonPhysicianRole)}
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(roleConfig) as NonPhysicianRole[]).map((role) => (
                <SelectItem key={role} value={role}>
                  {roleConfig[role].name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={effectiveRole}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {/* Quarterly Schedule Table */}
          <DataTable className="mb-6">
            <DataTableHeader>
              <DataTableRow>
                <DataTableCell header>Measure</DataTableCell>
                <DataTableCell header align="center">Q1</DataTableCell>
                <DataTableCell header align="center">Q2</DataTableCell>
                <DataTableCell header align="center">Q3</DataTableCell>
                <DataTableCell header align="center">Q4</DataTableCell>
              </DataTableRow>
            </DataTableHeader>
            <DataTableBody>
              {allMeasures.map((measure) => (
                <DataTableRow key={measure}>
                  <DataTableCell className="font-medium">{measure}</DataTableCell>
                  {quarters.map((q) => (
                    <DataTableCell key={q} align="center">
                      <MeasureIndicator
                        active={outcomeMeasuresByQuarter[q].includes(measure)}
                      />
                    </DataTableCell>
                  ))}
                </DataTableRow>
              ))}
              <DataTableRow highlight>
                <DataTableCell className="font-bold text-eventus-navy">
                  Measure Count
                </DataTableCell>
                {quarters.map((q) => (
                  <DataTableCell key={q} align="center" className="font-bold">
                    {outcomeMeasuresByQuarter[q].length}
                  </DataTableCell>
                ))}
              </DataTableRow>
              <DataTableRow highlight>
                <DataTableCell className="font-bold text-eventus-navy">
                  Weight Each
                </DataTableCell>
                {quarters.map((q) => {
                  const count = outcomeMeasuresByQuarter[q].length;
                  return (
                    <DataTableCell key={q} align="center">
                      <HighlightValue color="pink">
                        {count > 0
                          ? (60 / count).toFixed(60 % count === 0 ? 0 : 1) + '%'
                          : '—'}
                      </HighlightValue>
                    </DataTableCell>
                  );
                })}
              </DataTableRow>
            </DataTableBody>
          </DataTable>
        </motion.div>
      </AnimatePresence>

      {/* Single Measure Warning */}
      {allQuartersSingleMeasure && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">
              <strong>{config.name}</strong> has a single Outcome Measure all year, representing 60% of the incentive opportunity.
            </p>
          </CardContent>
        </Card>
      )}
    </Section>
  );
}

// Maximum Award Section
function MaximumAwardSection() {
  const { lockedRoleId, employmentStatus } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;
  const isTeletriage = lockedRoleId === 'teletriage';
  const isPartTime = employmentStatus === 'part-time';
  const isFullTime = employmentStatus === 'full-time';

  const [daysPerWeek, setDaysPerWeek] = useState(3);
  const [hoursPerWeek, setHoursPerWeek] = useState(30);
  const apcAward = daysPerWeek * 600;
  const teletraigeAward = Math.round(hoursPerWeek * 37.5);

  // Locked: section description is role-specific
  const sectionDescription = config
    ? 'Your maximum award is based on your employment status. Performance determines how much of that ceiling is earned.'
    : 'Employment status sets the ceiling for award potential. Performance determines how much of that ceiling is earned.';

  return (
    <Section id="maximum-award" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Maximum Award by Employment
      </h2>
      <p className="text-gray-600 mb-6">
        {sectionDescription}
      </p>

      {/* Maximum Award Tables — show both when unlocked, only relevant one when locked */}
      <div className={cn('mb-6', config ? '' : 'grid md:grid-cols-2 gap-6')}>
        {/* APC Card — shown when unlocked OR when locked to an APC role */}
        {(!config || !isTeletriage) && (
          <Card className="border-t-4 border-t-eventus-teal">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Stethoscope className="w-5 h-5 text-eventus-teal" />
                {config ? config.name : 'APCs'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableCell header>Employment</DataTableCell>
                    <DataTableCell header align="center">Days</DataTableCell>
                    <DataTableCell header align="right">Max Award</DataTableCell>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {!isPartTime && (
                    <DataTableRow highlight>
                      <DataTableCell className="font-medium">Full-Time</DataTableCell>
                      <DataTableCell align="center">—</DataTableCell>
                      <DataTableCell align="right">
                        <HighlightValue>$3,000</HighlightValue>
                      </DataTableCell>
                    </DataTableRow>
                  )}
                  {!isFullTime && (
                    <>
                      <DataTableRow>
                        <DataTableCell className="font-medium">Part-Time</DataTableCell>
                        <DataTableCell align="center">4</DataTableCell>
                        <DataTableCell align="right">$2,400</DataTableCell>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableCell className="font-medium">Part-Time</DataTableCell>
                        <DataTableCell align="center">3</DataTableCell>
                        <DataTableCell align="right">$1,800</DataTableCell>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableCell className="font-medium">Part-Time</DataTableCell>
                        <DataTableCell align="center">2</DataTableCell>
                        <DataTableCell align="right">$1,200</DataTableCell>
                      </DataTableRow>
                    </>
                  )}
                </DataTableBody>
              </DataTable>
            </CardContent>
          </Card>
        )}

        {/* Teletriage Card — shown when unlocked OR when locked to teletriage */}
        {(!config || isTeletriage) && (
          <Card className="border-t-4 border-t-eventus-navy">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Phone className="w-5 h-5 text-eventus-navy" />
                Teletriage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableCell header>Employment</DataTableCell>
                    <DataTableCell header align="center">Hrs/Week</DataTableCell>
                    <DataTableCell header align="right">Max Award</DataTableCell>
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {!isPartTime && (
                    <DataTableRow highlight>
                      <DataTableCell className="font-medium">Full-Time</DataTableCell>
                      <DataTableCell align="center">—</DataTableCell>
                      <DataTableCell align="right">
                        <HighlightValue color="blue">$1,500</HighlightValue>
                      </DataTableCell>
                    </DataTableRow>
                  )}
                  {!isFullTime && (
                    <>
                      <DataTableRow>
                        <DataTableCell className="font-medium">Part-Time</DataTableCell>
                        <DataTableCell align="center">32</DataTableCell>
                        <DataTableCell align="right">$1,200</DataTableCell>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableCell className="font-medium">Part-Time</DataTableCell>
                        <DataTableCell align="center">24</DataTableCell>
                        <DataTableCell align="right">$900</DataTableCell>
                      </DataTableRow>
                      <DataTableRow>
                        <DataTableCell className="font-medium">Part-Time</DataTableCell>
                        <DataTableCell align="center">16</DataTableCell>
                        <DataTableCell align="right">$600</DataTableCell>
                      </DataTableRow>
                    </>
                  )}
                </DataTableBody>
              </DataTable>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Part-time Calculators — hidden when full-time; show both when unlocked, only relevant one when locked */}
      {!isFullTime && (
      <Card className="border-2">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Part-Time Calculator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* APC Slider — shown when unlocked OR when locked to an APC role */}
          {(!config || !isTeletriage) && (
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 font-medium">
                  {config ? `${config.name} - Days worked per week:` : 'APC - Days worked per week:'}
                </span>
                <span className="font-bold text-eventus-teal text-xl">{daysPerWeek}</span>
              </div>
              <Slider
                value={[daysPerWeek]}
                onValueChange={([v]) => setDaysPerWeek(v)}
                min={1}
                max={5}
                step={1}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1</span>
                <span>2</span>
                <span>3</span>
                <span>4</span>
                <span>5</span>
              </div>
              <div className="bg-eventus-teal/5 rounded-xl p-4 text-center mt-3">
                <div className="text-base text-gray-600 mb-1">
                  {config ? 'Your Maximum' : 'APC Maximum'}
                </div>
                <div className="text-3xl font-bold text-eventus-teal">
                  ${apcAward.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  $600 x {daysPerWeek} days
                </div>
              </div>
            </div>
          )}

          {/* Divider only shown between two sliders (unlocked mode) */}
          {!config && <div className="border-t border-gray-200" />}

          {/* Teletriage Slider — shown when unlocked OR when locked to teletriage */}
          {(!config || isTeletriage) && (
            <div>
              <div className="flex justify-between mb-2">
                <span className="text-gray-600 font-medium">
                  {config ? 'Hours worked per week:' : 'Teletriage - Hours worked per week:'}
                </span>
                <span className="font-bold text-eventus-navy text-xl">{hoursPerWeek}</span>
              </div>
              <Slider
                value={[hoursPerWeek]}
                onValueChange={([v]) => setHoursPerWeek(v)}
                min={8}
                max={39}
                step={1}
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>8</span>
                <span>16</span>
                <span>24</span>
                <span>32</span>
                <span>39</span>
              </div>
              <div className="bg-eventus-navy/5 rounded-xl p-4 text-center mt-3">
                <div className="text-base text-gray-600 mb-1">
                  {config ? 'Your Maximum' : 'Teletriage Maximum'}
                </div>
                <div className="text-3xl font-bold text-eventus-navy">
                  ${teletraigeAward.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  $37.50 x {hoursPerWeek} hrs
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Schedule ceiling note removed per COO feedback */}
    </Section>
  );
}

// Measure Definitions Section
function MeasureDefinitionsSection() {
  const { lockedRoleId, scheduleItems } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;
  const scheduleRole = lockedRoleId ? lockedRoleId as ScheduleRole : null;

  const allOutcomeMeasures = [
    {
      abbrev: 'AWV',
      name: 'Annual Wellness Visits',
      description: 'AWV completion for attributed patients',
      threshold: '90%',
      roles: 'Primary Care APC',
    },
    {
      abbrev: 'A1C',
      name: 'Diabetes A1C Testing',
      description: 'A1C testing for diabetic patients',
      threshold: '90%',
      roles: 'Primary Care APC',
    },
    {
      abbrev: 'BCS',
      name: 'Breast Cancer Screening',
      description: 'Mammogram completion for eligible patients',
      threshold: '90%',
      roles: 'Primary Care APC',
    },
    {
      abbrev: 'CRCS',
      name: 'Colorectal Cancer Screening',
      description: 'Colonoscopy/FIT completion for eligible patients',
      threshold: '90%',
      roles: 'Primary Care APC',
    },
    {
      abbrev: 'PHQ-9',
      name: 'Depression Screening',
      description: 'Mental health screening completion',
      threshold: '90%',
      roles: 'Primary Care APC, Psychiatry APC',
    },
    {
      abbrev: 'HTN',
      name: 'Hypertension Control',
      description: 'Blood pressure documentation',
      threshold: '90%',
      roles: 'Primary Care APC',
    },
    {
      abbrev: 'ACP',
      name: 'Advance Care Planning',
      description: 'ACP documentation for palliative patients',
      threshold: '90%',
      roles: 'Palliative Care APC',
    },
    {
      abbrev: 'ED Send-Out',
      name: 'ED Send-Out Rate',
      description: 'Percentage of calls resulting in ED referral (lower is better)',
      threshold: '<25%',
      roles: 'Teletriage',
    },
  ];

  const allConsistencyMeasures = [
    {
      abbrev: 'PIMs',
      name: 'Medication Safety',
      description: 'Prescribing patterns for potentially inappropriate medications',
      threshold: '90%',
      roles: 'All APCs (not Teletriage)',
    },
    {
      abbrev: 'Coding',
      name: 'Coding Accuracy',
      description: 'Results from coding audits',
      threshold: '90%',
      roles: 'All roles',
    },
    {
      abbrev: 'Sig Stnds',
      name: 'Signature Standards',
      description: 'Results from quality/signature audits',
      threshold: '90%',
      roles: 'All roles',
    },
  ];

  // When locked: derive relevant measures from the schedule
  const relevantOutcomeAbbrevs = scheduleRole
    ? new Set(
        ALL_QUARTERS.flatMap(q =>
          getRoleQuarterOutcomeMeasures(scheduleItems, scheduleRole, q)
        )
      )
    : null;
  const outcomeMeasures = (relevantOutcomeAbbrevs
    ? allOutcomeMeasures.filter((m) => relevantOutcomeAbbrevs.has(m.abbrev))
    : allOutcomeMeasures
  ).map((m) => {
    // When a role is locked, derive the display threshold from the canonical schedule
    // rather than showing the hardcoded static string. Use Q4 as representative
    // (non-physician measures have the same threshold across quarters).
    if (!scheduleRole) return m;
    const t = getMeasureThresholdDisplay(scheduleItems, scheduleRole, m.abbrev, 'Q4')
      ?? getMeasureThresholdDisplay(scheduleItems, scheduleRole, m.abbrev, 'Q1');
    return t !== undefined ? { ...m, threshold: `${t}%` } : m;
  });

  const relevantConsistencyNames = scheduleRole
    ? new Set(getRoleConsistencyMeasures(scheduleItems, scheduleRole))
    : null;
  const consistencyMeasures = relevantConsistencyNames
    ? allConsistencyMeasures.filter((m) => relevantConsistencyNames.has(m.abbrev))
    : allConsistencyMeasures;

  // Bottom text: role-specific when locked, cross-role when unlocked
  const consistencyCount = relevantConsistencyNames?.size ?? 0;
  const consistencyBottomText = config && consistencyCount > 0
    ? `Each consistency measure is worth ${(40 / consistencyCount).toFixed(2)}% (40% / ${consistencyCount}).`
    : config
      ? `Each consistency measure is worth ${(40 / (config.hasPIMs ? 3 : 2)).toFixed(2)}% (40% / ${config.hasPIMs ? 3 : 2}).`
      : 'APCs: Each consistency measure is worth 13.33% (40% / 3). Teletriage: Each is worth 20% (40% / 2, no PIMs).';

  return (
    <Section id="measure-definitions">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Measure Definitions
      </h2>
      <p className="text-gray-600 mb-6">
        {config
          ? `Reference for your Outcome and Consistency Measures.`
          : 'Complete reference for all Outcome and Consistency Measures.'}
      </p>

      {/* Outcome Measures Accordion */}
      <h3 className="text-lg font-semibold text-eventus-navy mb-3 flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-pink-500" />
        Outcome Measures (60%)
      </h3>
      <Accordion type="single" collapsible className="mb-8">
        {outcomeMeasures.map((measure) => (
          <AccordionItem key={measure.abbrev} value={measure.abbrev}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="font-mono">
                  {measure.abbrev}
                </Badge>
                <span className="font-medium">{measure.name}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pl-4 space-y-2 text-sm">
                <p className="text-gray-600">{measure.description}</p>
                <div className="flex flex-wrap gap-4">
                  {lockedRoleId !== 'primary-care-apc' && (
                    <span>
                      <strong>Threshold:</strong> {measure.threshold}
                    </span>
                  )}
                  {/* Roles label hidden when locked — redundant in single-role view */}
                  {!config && (
                    <span>
                      <strong>Roles:</strong> {measure.roles}
                    </span>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Consistency Measures Accordion */}
      <h3 className="text-lg font-semibold text-eventus-navy mb-3 flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-blue-500" />
        Consistency Measures (40%)
      </h3>
      <Accordion type="single" collapsible>
        {consistencyMeasures.map((measure) => (
          <AccordionItem key={measure.abbrev} value={measure.abbrev}>
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <Badge variant="outline" className="font-mono">
                  {measure.abbrev}
                </Badge>
                <span className="font-medium">{measure.name}</span>
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="pl-4 space-y-2 text-sm">
                <p className="text-gray-600">{measure.description}</p>
                <div className="flex flex-wrap gap-4">
                  {lockedRoleId !== 'primary-care-apc' && (
                    <span>
                      <strong>Threshold:</strong> {measure.threshold}
                    </span>
                  )}
                  {/* Roles label hidden when locked — redundant in single-role view */}
                  {!config && (
                    <span>
                      <strong>Roles:</strong> {measure.roles}
                    </span>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <p className="text-base text-gray-500 mt-6">
        {consistencyBottomText}
      </p>
    </Section>
  );
}

// 90% Threshold Section
function NinetyPercentSection() {
  const { lockedRoleId } = useNonPhysicianContext();
  const isTeletriage = lockedRoleId === 'teletriage';
  const isPrimaryCareAPC = lockedRoleId === 'primary-care-apc';
  // Hide threshold numbers for primary-care-apc (per 2026 comms — targets ramp quarterly)
  if (isPrimaryCareAPC) {
    return null;
  }
  // When locked to teletriage: show <25% gauge; when locked to APC or unlocked: show 90% gauge
  const showTeletriaageGauge = isTeletriage;
  // Exception callout: show only when unlocked (leadership mode); hidden for both locked APC and locked Teletriage
  const showExceptionCallout = !lockedRoleId;

  return (
    <Section id="ninety-percent" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        {showTeletriaageGauge ? 'The 25% Threshold' : 'The 90% Threshold'}
      </h2>
      <p className="text-gray-600 mb-6">
        {showTeletriaageGauge
          ? 'ED Send-Out rate must be BELOW 25%. Lower is better. Meet this threshold and earn the full Outcomes portion.'
          : 'This applies to all Outcome and Consistency measures. Meet or exceed the threshold for the measure = earn that portion. Do not meet the threshold for the measure = earn 0% for that measure only.'}
      </p>

      {/* 90% Gauge — shown for APCs (locked or unlocked) */}
      {!showTeletriaageGauge && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <div className="text-center mb-4">
            <Gauge className="w-8 h-8 text-eventus-navy mx-auto mb-2" />
            <h3 className="text-lg font-bold text-eventus-navy">THE 90% THRESHOLD</h3>
          </div>

          {/* Scale labels */}
          <div className="flex justify-between text-base text-gray-500 mb-2">
            <span>0%</span>
            <span className="font-bold text-eventus-navy">90%</span>
            <span>100%</span>
          </div>

          {/* The gauge bar */}
          <div className="relative h-12 rounded-lg overflow-hidden flex">
            <div className="w-[90%] bg-gray-200 flex items-center justify-center">
              <span className="text-gray-600 font-bold">UNMET</span>
            </div>
            <div className="w-[10%] bg-green-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">MET</span>
            </div>
            {/* Threshold marker */}
            <div className="absolute top-0 bottom-0 left-[90%] border-l-4 border-eventus-navy" />
          </div>

          {/* Result labels */}
          <div className="flex mt-3">
            <div className="w-[90%] text-center">
              <div className="text-red-600 font-medium text-sm">Earn 0%</div>
              <div className="text-xs text-gray-500">for this measure</div>
            </div>
            <div className="w-[10%] text-center">
              <div className="text-green-600 font-medium text-sm">Full</div>
              <div className="text-xs text-gray-500">weight</div>
            </div>
          </div>
        </div>
      )}

      {/* <25% Gauge — shown only for locked Teletriage */}
      {showTeletriaageGauge && (
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6">
          <div className="text-center mb-4">
            <Gauge className="w-8 h-8 text-eventus-navy mx-auto mb-2" />
            <h3 className="text-lg font-bold text-eventus-navy">THE 25% THRESHOLD</h3>
          </div>

          <p className="text-sm text-gray-600 text-center mb-4">
            ED Send-Out rate must be <strong>BELOW 25%</strong>. Lower is better.
          </p>

          {/* Scale labels */}
          <div className="flex justify-between text-base text-gray-500 mb-2">
            <span>0%</span>
            <span className="font-bold text-eventus-navy">25%</span>
            <span>100%</span>
          </div>

          {/* The gauge bar — inverted: MET zone is 0-25% (left), UNMET is 25-100% (right) */}
          <div className="relative h-12 rounded-lg overflow-hidden flex">
            <div className="w-[25%] bg-green-500 flex items-center justify-center">
              <span className="text-white font-bold text-xs">MET</span>
            </div>
            <div className="w-[75%] bg-gray-200 flex items-center justify-center">
              <span className="text-gray-600 font-bold">UNMET</span>
            </div>
            {/* Threshold marker */}
            <div className="absolute top-0 bottom-0 left-[25%] border-l-4 border-eventus-navy" />
          </div>

          {/* Result labels */}
          <div className="flex mt-3">
            <div className="w-[25%] text-center">
              <div className="text-green-600 font-medium text-sm">Earn full</div>
              <div className="text-xs text-gray-500">weight</div>
            </div>
            <div className="w-[75%] text-center">
              <div className="text-red-600 font-medium text-sm">Earn 0%</div>
              <div className="text-xs text-gray-500">for this measure</div>
            </div>
          </div>
        </div>
      )}

      {/* Exception Callout — shown only in leadership (unlocked) mode */}
      {showExceptionCallout && (
        <Card className="bg-amber-50 border-amber-200 mb-6">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 mb-1">Exception: ED Send-Out (Teletriage)</p>
              <p className="text-amber-700 text-sm">
                Must be <strong>BELOW 25%</strong> to meet the measure. Lower is better for ED send-outs.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

    </Section>
  );
}

// Calculation Steps Section
function CalculationStepsSection() {
  const { lockedRoleId, employmentStatus, scheduleItems } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;
  const scheduleRole = lockedRoleId ? lockedRoleId as ScheduleRole : null;
  const consistencyMeasureNames = scheduleRole
    ? getRoleConsistencyMeasures(scheduleItems, scheduleRole)
    : [];
  // Fall back to hasPIMs when schedule not yet loaded
  const consistencyCount = consistencyMeasureNames.length > 0
    ? consistencyMeasureNames.length
    : config ? (config.hasPIMs ? 3 : 2) : 3;
  const consistencyLabel = consistencyMeasureNames.length > 0
    ? consistencyMeasureNames.join(', ')
    : config ? (config.hasPIMs ? 'PIMs, Coding, Sig Stnds' : 'Coding, Sig Stnds') : '';

  // Step 1 description — employment-aware when locked
  const ptRate = config
    ? config.partTimeUnit === 'hour'
      ? `$${config.maxAward.perHourPartTime ?? config.maxAward.perDayPartTime}/${config.partTimeUnit}`
      : `$${config.maxAward.perDayPartTime}/${config.partTimeUnit}`
    : null;
  const step1Description = config
    ? employmentStatus === 'part-time'
      ? `Based on your ${config.partTimeUnit === 'hour' ? 'hours' : 'days'} worked: ${ptRate} x ${config.partTimeUnit === 'hour' ? 'hours' : 'days'} per week`
      : employmentStatus === 'full-time'
        ? `Based on your full-time status: $${config.maxAward.fullTime.toLocaleString()} per quarter`
        : `Based on your ${config.partTimeUnit === 'hour' ? 'hours' : 'days'} worked: Full-time = $${config.maxAward.fullTime.toLocaleString()}, Part-time = ${ptRate}`
    : 'Based on role and employment status: APC FT = $3,000, Teletriage FT = $1,500, Part-time = multiplier x days/week';

  // Step 2 description — role-specific when locked
  const step2Description = config
    ? config.id === 'teletriage'
      ? 'ED Send-Out rate must be below 25% to earn the 60%.'
      : config.id === 'primary-care-apc'
        ? 'Each outcome measure that meets its quarterly target earns its share of the 60%.'
        : 'Each outcome measure at or above 90% earns its share of the 60%.'
    : 'For each active outcome measure: >=90% (or <25% for ED Send-Out) earns that measure\'s %. Below threshold earns 0%.';

  // Step 3 description — role-specific when locked
  const step3Description = config
    ? config.id === 'primary-care-apc'
      ? `Each consistency measure (${consistencyLabel}) that meets its target earns ${(40 / consistencyCount).toFixed(consistencyCount === 2 ? 0 : 2)}% each.`
      : `Each consistency measure (${consistencyLabel}) at or above 90% earns ${(40 / consistencyCount).toFixed(consistencyCount === 2 ? 0 : 2)}% each.`
    : 'For each consistency measure: >=90% earns that measure\'s %. APCs: 13.33% each. Teletriage: 20% each.';

  const steps = [
    {
      number: 1,
      title: 'Determine Maximum Award',
      description: step1Description,
    },
    {
      number: 2,
      title: 'Calculate Outcome Percentage',
      description: step2Description,
    },
    {
      number: 3,
      title: 'Calculate Consistency Percentage',
      description: step3Description,
    },
    {
      number: 4,
      title: 'Calculate Final Award',
      description: 'Formula: (Outcome % + Consistency %) x Maximum Award = Final Award',
    },
  ];

  return (
    <Section id="calculation-steps">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        How Awards Are Calculated
      </h2>
      <p className="text-gray-600 mb-6">
        Four steps to calculate any award. Use this to walk through examples with your team.
      </p>

      <div className="space-y-4">
        {steps.map((step) => (
          <div
            key={step.number}
            className="flex items-start gap-4 bg-white rounded-xl p-4 border border-gray-200"
          >
            <div className="w-10 h-10 rounded-full bg-eventus-teal flex items-center justify-center flex-shrink-0 text-white font-bold">
              {step.number}
            </div>
            <div>
              <h3 className="font-semibold text-eventus-navy">{step.title}</h3>
              <p className="text-base text-gray-600 mt-1">{step.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Formula highlight */}
      <div className="mt-6 p-4 bg-eventus-teal/10 rounded-xl text-center">
        <p className="font-mono text-eventus-navy">
          <span className="text-pink-600">Outcome %</span> +{' '}
          <span className="text-blue-600">Consistency %</span> ={' '}
          <span className="text-eventus-teal font-bold">Total %</span>
        </p>
        <p className="font-mono text-eventus-navy mt-2">
          <span className="text-eventus-teal font-bold">Total %</span> x{' '}
          <span className="text-green-600">Maximum Award</span> ={' '}
          <span className="font-bold">Final Award</span>
        </p>
      </div>
    </Section>
  );
}

// Payout Scenarios Section
function PayoutScenariosSection() {
  const { employmentStatus } = useNonPhysicianContext();
  const scenarioDescription = employmentStatus === 'part-time'
    ? 'All scenarios assume part-time employment. Actual amounts scale with your schedule.'
    : 'All scenarios assume full-time, productivity-based employment.';

  const scenarios = [
    {
      scenario: 'Perfect Quarter (PC APC)',
      outcomes: '6/6 (60%)',
      consistency: '3/3 (40%)',
      total: '100%',
      award: '$3,000',
      highlight: true,
    },
    {
      scenario: 'One Outcome Unmet (PC APC Q4)',
      outcomes: '5/6 (50%)',
      consistency: '3/3 (40%)',
      total: '90%',
      award: '$2,700',
    },
    {
      scenario: 'Single Measure Unmet (Psych APC)',
      outcomes: '0/1 (0%)',
      consistency: '3/3 (40%)',
      total: '40%',
      award: '$1,200',
      note: 'Consistency floor',
    },
    {
      scenario: 'Perfect Teletriage',
      outcomes: '1/1 (60%)',
      consistency: '2/2 (40%)',
      total: '100%',
      award: '$1,500',
      highlight: true,
    },
    {
      scenario: 'Teletriage ED Unmet',
      outcomes: '0/1 (0%)',
      consistency: '2/2 (40%)',
      total: '40%',
      award: '$600',
      note: 'Consistency floor',
    },
  ];

  return (
    <Section id="payout-scenarios" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Payout Scenarios
      </h2>
      <p className="text-gray-600 mb-6">
        Multiple scenarios visible at once for comparison. {scenarioDescription}
      </p>

      <DataTable>
        <DataTableHeader>
          <DataTableRow>
            <DataTableCell header>Scenario</DataTableCell>
            <DataTableCell header align="center">Outcomes</DataTableCell>
            <DataTableCell header align="center">Consistency</DataTableCell>
            <DataTableCell header align="center">Total %</DataTableCell>
            <DataTableCell header align="right">Award</DataTableCell>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          {scenarios.map((s) => (
            <DataTableRow key={s.scenario} highlight={s.highlight}>
              <DataTableCell className="font-medium">
                {s.scenario}
                {s.note && (
                  <span className="text-xs text-blue-600 ml-2">({s.note})</span>
                )}
              </DataTableCell>
              <DataTableCell align="center" className="text-pink-600">
                {s.outcomes}
              </DataTableCell>
              <DataTableCell align="center" className="text-blue-600">
                {s.consistency}
              </DataTableCell>
              <DataTableCell align="center" className="font-semibold">
                {s.total}
              </DataTableCell>
              <DataTableCell align="right">
                <HighlightValue color={s.highlight ? 'green' : 'purple'}>
                  {s.award}
                </HighlightValue>
              </DataTableCell>
            </DataTableRow>
          ))}
        </DataTableBody>
      </DataTable>

    </Section>
  );
}

// Interactive Calculator Section
function InteractiveCalculatorSection() {
  const { lockedRoleId, employmentStatus } = useNonPhysicianContext();
  return (
    <Section id="interactive-calculator">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-2">
        Interactive Calculator
      </h2>
      <p className="text-gray-600 mb-6">
        Practice scenarios for team discussions. Click any measure to toggle measure met/unmet.
      </p>
      <PillarCalculator track="non-physician" lockedRole={lockedRoleId} effectiveEmploymentStatus={employmentStatus} />
    </Section>
  );
}

// Scenario Presets Section
function ScenarioPresetsSection() {
  const { lockedRoleId, employmentStatus } = useNonPhysicianContext();
  return (
    <Section id="scenario-presets" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-2">
        Pre-Built Scenarios
      </h2>
      <p className="text-gray-600 mb-6">
        These are scenarios you can walk through with your team to build intuition.
      </p>
      <ScenarioPresets track="non-physician" lockedRole={lockedRoleId} employmentStatus={employmentStatus} />
    </Section>
  );
}

// Risk Profile Section
function RiskProfileSection() {
  const { lockedRoleId, scheduleItems } = useNonPhysicianContext();
  const config = lockedRoleId ? roleConfig[lockedRoleId] : null;
  const scheduleRole = lockedRoleId ? lockedRoleId as ScheduleRole : null;

  // Dynamic calculations derived from schedule when locked
  const q1Count = scheduleRole ? getRoleQuarterOutcomeMeasures(scheduleItems, scheduleRole, 'Q1').length : null;
  const q3Count = scheduleRole ? getRoleQuarterOutcomeMeasures(scheduleItems, scheduleRole, 'Q3').length : null;
  const q4Count = scheduleRole ? getRoleQuarterOutcomeMeasures(scheduleItems, scheduleRole, 'Q4').length : null;
  const oneUnmetLoss = config && q4Count && q4Count > 0
    ? `-$${Math.round(config.maxAward.fullTime * 0.6 / q4Count).toLocaleString()} (${(60 / q4Count).toFixed(0)}%)`
    : null;
  const allOutcomesLoss = config
    ? `-$${Math.round(config.maxAward.fullTime * 0.6).toLocaleString()} (60%)`
    : null;

  // Callout messaging — single-measure vs multi-measure roles
  const isSingleMeasureRole = q4Count !== null ? q4Count === 1 : false;
  const calloutText = config
    ? isSingleMeasureRole
      ? 'You have one focused Outcome Measure representing 60% of your incentive. Consistency measures provide additional incentive award opportunity even if your outcome measure falls short.'
      : 'Your outcome measures are diversified across multiple quality areas. Missing one measure has a smaller proportional impact than single-measure roles.'
    : 'Psychiatry, Palliative, and Teletriage have one focused Outcome Measure (60% of incentive). Help these teams understand that Consistency Measures (40%) provide additional incentive award opportunity.';

  const calloutTitle = config
    ? isSingleMeasureRole ? 'Focused Outcome Measure' : 'Diversified Outcome Measures'
    : 'Specialty Measure Focus';

  return (
    <Section id="risk-profile">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        {config ? 'Your Risk Profile' : 'Risk Profile'}
      </h2>
      <p className="text-gray-600 mb-6">
        {config
          ? 'Understanding your risk profile helps you plan which measures to prioritize.'
          : 'Some roles have diversified risk, others do not. Understanding this helps set expectations.'}
      </p>

      <DataTable className="mb-6">
        <DataTableHeader>
          <DataTableRow>
            <DataTableCell header>Role</DataTableCell>
            <DataTableCell header align="center">Q1-Q3 Measures</DataTableCell>
            <DataTableCell header align="center">Q4 Measures</DataTableCell>
            <DataTableCell header align="center">One Unmet Measure (Q4)</DataTableCell>
            <DataTableCell header align="center">All Outcomes Unmet</DataTableCell>
          </DataTableRow>
        </DataTableHeader>
        <DataTableBody>
          {/* When locked: show only the locked role's row */}
          {config ? (
            <DataTableRow highlight>
              <DataTableCell className="font-medium">{config.name}</DataTableCell>
              <DataTableCell align="center">
                {q1Count === q3Count
                  ? (q1Count ?? '—')
                  : `${q1Count ?? '—'}-${q3Count ?? '—'}`}
              </DataTableCell>
              <DataTableCell align="center">{q4Count ?? '—'}</DataTableCell>
              <DataTableCell align="center" className={isSingleMeasureRole ? 'text-red-600' : 'text-amber-600'}>
                {oneUnmetLoss}
              </DataTableCell>
              <DataTableCell align="center" className="text-red-600">
                {allOutcomesLoss}
              </DataTableCell>
            </DataTableRow>
          ) : (
            <>
              <DataTableRow>
                <DataTableCell className="font-medium">Primary Care APC</DataTableCell>
                <DataTableCell align="center">1-4</DataTableCell>
                <DataTableCell align="center">6</DataTableCell>
                <DataTableCell align="center" className="text-amber-600">
                  -$300 (10%)
                </DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$1,800 (60%)
                </DataTableCell>
              </DataTableRow>
              <DataTableRow>
                <DataTableCell className="font-medium">Psychiatry APC</DataTableCell>
                <DataTableCell align="center">1</DataTableCell>
                <DataTableCell align="center">1</DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$1,800 (60%)
                </DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$1,800 (60%)
                </DataTableCell>
              </DataTableRow>
              <DataTableRow>
                <DataTableCell className="font-medium">Palliative APC</DataTableCell>
                <DataTableCell align="center">1</DataTableCell>
                <DataTableCell align="center">1</DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$1,800 (60%)
                </DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$1,800 (60%)
                </DataTableCell>
              </DataTableRow>
              <DataTableRow>
                <DataTableCell className="font-medium">Teletriage</DataTableCell>
                <DataTableCell align="center">1</DataTableCell>
                <DataTableCell align="center">1</DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$900 (60%)
                </DataTableCell>
                <DataTableCell align="center" className="text-red-600">
                  -$900 (60%)
                </DataTableCell>
              </DataTableRow>
            </>
          )}
        </DataTableBody>
      </DataTable>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-blue-800 mb-1">{calloutTitle}</p>
            <p className="text-blue-700 text-sm">
              {calloutText}
            </p>
          </div>
        </CardContent>
      </Card>
    </Section>
  );
}

// Key Messages Section
function KeyMessagesSection() {
  const { lockedRoleId, employmentStatus } = useNonPhysicianContext();
  const isLocked = !!lockedRoleId;

  const baseMessages = [
    {
      title: 'Each measure functions independently.',
      message: 'Each measure stands alone. Meet a measure and earn that portion. Strong performance in one area supports your overall success.',
      icon: Ban,
      color: 'green',
    },
    {
      title: 'Same rules for everyone.',
      message: 'The 60/40 model applies to all roles and employment levels. No special treatment, no hidden rules.',
      icon: Scale,
      color: 'blue',
    },
    {
      title: isLocked ? 'Your patients, your metrics.' : 'Their patients, their metrics.',
      message: isLocked
        ? "You are measured on outcomes for patients assigned to you - not the team average, not someone else's panel."
        : "Clinicians are measured on outcomes for patients assigned to them - not the team average, not someone else's panel.",
      icon: Target,
      color: 'purple',
    },
    {
      title: employmentStatus === 'part-time'
        ? 'Your award ceiling is proportional to your schedule.'
        : 'Part-time employees have the potential to receive a proportional incentive.',
      message: employmentStatus === 'part-time'
        ? 'Your maximum award scales with your days or hours worked. Same quality standards apply regardless of schedule.'
        : isLocked
          ? "Part-time clinicians have the potential to receive a proportional incentive."
          : "Lower potential ceiling for incentive, same quality standards. Part-timers aren't penalized - they're scaled proportionally.",
      icon: Users,
      color: 'teal',
    },
  ];

  // When full-time: filter out the part-time message
  const messages = employmentStatus === 'full-time'
    ? baseMessages.filter((_, index) => index !== baseMessages.length - 1)
    : baseMessages;

  const colorClasses = {
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'bg-green-100 text-green-600',
      title: 'text-green-800',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'bg-blue-100 text-blue-600',
      title: 'text-blue-800',
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'bg-purple-100 text-purple-600',
      title: 'text-purple-800',
    },
    teal: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      icon: 'bg-teal-100 text-teal-600',
      title: 'text-teal-800',
    },
  };

  return (
    <Section id="key-messages" background="gray">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        {isLocked ? 'Key Messages' : 'Key Messages for Your Team'}
      </h2>
      <p className="text-gray-600 mb-6">
        {isLocked
          ? 'The key principles behind how EQuIP works for you.'
          : 'These four messages are designed to proactively address the most common questions and concerns APCs.'}
      </p>

      <div className="space-y-3">
        {messages.map((msg) => {
          const colors = colorClasses[msg.color as keyof typeof colorClasses];
          return (
            <div
              key={msg.title}
              className={cn('flex items-start gap-4 p-4 rounded-xl border-l-4', colors.bg, colors.border)}
            >
              <div
                className={cn(
                  'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                  colors.icon
                )}
              >
                <msg.icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className={cn('font-bold', colors.title)}>
                  {msg.title}
                </h3>
                <p className="text-gray-600 text-sm mt-0.5">{msg.message}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// Next Steps Section
function NextStepsSection() {
  return (
    <Section id="next-steps">
      <h2 className="text-2xl md:text-3xl font-bold text-eventus-navy mb-4">
        Continue Your Preparation
      </h2>

      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/non-physician/leader-guide">
          <Card className="h-full border-2 border-eventus-teal/20 hover:border-eventus-teal/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-teal/10 flex items-center justify-center mb-4 group-hover:bg-eventus-teal transition-colors">
                <Book className="w-6 h-6 text-eventus-teal group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">
                Leader Guide
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Detailed reference with interactive calculator and scenarios.
              </p>
              <div className="flex items-center text-eventus-teal font-semibold text-sm">
                View Leader Guide
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/non-physician/faq">
          <Card className="h-full border-2 border-eventus-purple/20 hover:border-eventus-purple/50 hover:shadow-lg transition-all cursor-pointer group">
            <CardContent className="p-6 flex flex-col items-center text-center">
              <div className="w-12 h-12 rounded-xl bg-eventus-purple/10 flex items-center justify-center mb-4 group-hover:bg-eventus-purple transition-colors">
                <HelpCircle className="w-6 h-6 text-eventus-purple group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-eventus-navy mb-2">FAQ</h3>
              <p className="text-gray-600 text-sm mb-3">
                Frameworks to help you answer the questions your team may ask.
              </p>
              <div className="flex items-center text-eventus-purple font-semibold text-sm">
                View FAQ
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>
    </Section>
  );
}

// Main Component
const LOCKED_HIDDEN_SECTIONS = new Set(['eligibility', 'role-comparison', 'payout-scenarios', 'next-steps']);

interface NonPhysicianFrameworkProps {
  lockedRoleId?: NonPhysicianRole;
  employmentStatus?: EmploymentStatus;
  navItems?: { id: string; label: string; path: string; icon?: React.ReactNode }[];
  showPersonaDropdown?: boolean;
  showHome?: boolean;
}

export function NonPhysicianFramework({ lockedRoleId, employmentStatus, navItems, showPersonaDropdown, showHome }: NonPhysicianFrameworkProps) {
  const { items: scheduleItems, isLoading: scheduleLoading } = useEquipMeasureScheduleYear(new Date().getFullYear());

  const visibleNavSections = lockedRoleId
    ? navSections
        .filter(s => !LOCKED_HIDDEN_SECTIONS.has(s.id))
        .filter(s => s.id !== 'risk-profile')
        // Hide the threshold nav item for primary-care-apc (per 2026 comms)
        .filter(s => !(lockedRoleId === 'primary-care-apc' && s.id === 'ninety-percent'))
        .map(s => {
          if (s.id === 'maximum-award') return { ...s, label: 'Your Award' };
          return s;
        })
    : navSections;

  return (
    <NonPhysicianFrameworkContext.Provider value={{ lockedRoleId, employmentStatus, scheduleItems, scheduleLoading }}>
      <div className="min-h-screen bg-gray-50 pb-20 lg:pb-0">
        {/* Floating Navigation */}
        <FloatingNav items={navItems ?? [
          { id: 'framework', label: 'Framework', path: '/non-physician/framework' },
          { id: 'leader-guide', label: 'Leader Guide', path: '/non-physician/leader-guide' },
          { id: 'faq', label: 'FAQ', path: '/non-physician/faq' },
          { id: 'leadership', label: 'Leadership Dashboard', path: '/leadership' },
        ]} showPersonaDropdown={showPersonaDropdown} showHome={showHome} />

        {/* Overlay Side Navigation */}
        <OverlaySideNav sections={visibleNavSections} />

        {/* Hero Section */}
        <HeroSection />

        {/* Quick Reference Card */}
        <Section id="quick-reference">
          <QuickReferenceCard />
        </Section>

        {/* Eligibility Section — hidden when locked (shows all-role award table) */}
        {!lockedRoleId && <APCEligibilitySection />}

        {/* The 60/40 Model */}
        <SixtyFortyModelSection />

        {/* Role Comparison — hidden when locked (shows cross-role data) */}
        {!lockedRoleId && <RoleComparisonSection />}

        {/* Quarterly Measure Schedule */}
        <QuarterlyScheduleSection />

        {/* Maximum Award */}
        <MaximumAwardSection />

        {/* Measure Definitions */}
        <MeasureDefinitionsSection />

        {/* The 90% Threshold */}
        <NinetyPercentSection />

        {/* How Awards Are Calculated */}
        <CalculationStepsSection />

        {/* Payout Scenarios — hidden when locked (cross-role scenarios) */}
        {!lockedRoleId && <PayoutScenariosSection />}

        {/* Interactive Calculator */}
        <InteractiveCalculatorSection />

        {/* Scenario Presets */}
        <ScenarioPresetsSection />

        {/* Risk Profile — leadership-only (hidden when locked to a specific role) */}
        {!lockedRoleId && <RiskProfileSection />}

        {/* Key Messages */}
        <KeyMessagesSection />

        {/* Next Steps — hidden when locked (links to leadership-only pages) */}
        {!lockedRoleId && <NextStepsSection />}
      </div>
    </NonPhysicianFrameworkContext.Provider>
  );
}
